import { SyntheticEvent, useContext, useState, useEffect } from "react";
import {
  Button,
  Upload,
  Table,
  Popconfirm,
  Image,
  Spin,
  Input,
  Typography,
  UploadFile,
  Select,
} from "antd";
import { UploadOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { ref, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { GoogleAuthProvider } from "firebase/auth";

import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/hljs";

import { AnalyzeImageEvent, AnalyzeImageResponse } from "./types";

import { FirebaseContext } from "../context/FirebaseContext";
import { useGoogleToken } from '../hooks/useGoogleToken';

const { TextArea } = Input;

const Workspace = () => {
  const firebase = useContext(FirebaseContext);
  const [uploadList, setUploadList] = useState([] as UploadFile[]);
  const [eventsList, setEventsList] = useState([] as AnalyzeImageEvent[]);
  const [previewImage, setPreviewImage] = useState("");
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [savingToGoogleSheet, setSavingToGoogleSheet] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [tagType, setTagType] = useState("ecommerce");

  useEffect(() => {
    const getPrompt = async () => {
      try {
        const promptResult = await firebase.functions.getPrompt();
        setPrompt((promptResult.data as any).prompt);
      } catch (error) {
        console.error("Error getting prompt", error);
      }
    };

    getPrompt();
  }, []);

  const columns = [
    {
      title: "Evento",
      dataIndex: "event",
      key: "event",
      editable: false,
      /*onCell: (record: any) => ({
                rowSpan: record.paramsCount,
            }),*/
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      editable: false,
      /*onCell: (record: any) => ({
                rowSpan: record.paramsCount,
            }),*/
    },
    {
      title: "Tag Script",
      dataIndex: "tag_script",
      key: "tag_script",
      editable: false,
      width: 300,
      render: (tag_script: any) => {
        return (
          <SyntaxHighlighter
            language="javascript"
            style={a11yDark}
            wrapLines={true}
            wrapLongLines={true}
          >
            {tag_script}
          </SyntaxHighlighter>
        );
      },
      /*onCell: (record: any) => ({
                rowSpan: record.paramsCount,
            }),*/
    },
    /*{
            title: "Parámetros",
            children: [
                {
                    title: "Nombre",
                    dataIndex: "param_name",
                    key: "param_name",
                },
                {
                    title: "Valor",
                    dataIndex: "param_value",
                    key: "param_value",
                },
                {
                    title: "Ejemplo",
                    dataIndex: "example_value",
                    key: "example_value",
                },
            ],
        },*/
    {
      title: "Parámetros",
      dataIndex: "parameters",
      key: "parameters",
      render: (parameters: any) => {
        return (
          <ul>
            {parameters.map((param: any, index: number) => (
              <li key={index}>
                <strong>{param.param_name}</strong>: {param.param_value}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      title: "Operación",
      dataIndex: "operation",
      render: (_: any, record: any) =>
        eventsList.length >= 1 ? (
          <Popconfirm
            title="¿Eliminar seguro?"
            onConfirm={() => handleDelete(record.key)}
          >
            <a>Eliminar</a>
          </Popconfirm>
        ) : null,
      /*onCell: (record: any) => ({
                rowSpan: record.paramsCount,
            }),*/
    },
  ];

  /**
   * Uploads an image to Firebase Storage.
   *
   * @param {Object} options - The options for the upload.
   * @param {Function} options.onSuccess - Callback function to be called on successful upload.
   * @param {Function} options.onError - Callback function to be called on upload error.
   * @param {File} options.file - The file to be uploaded.
   * @param {Function} options.onProgress - Callback function to be called to report upload progress.
   *
   * @returns {Promise<void>} - A promise that resolves when the upload is complete.
   *
   * @throws {Error} - Throws an error if the upload fails.
   */
  const uploadImage = async (options: any): Promise<void> => {
    const onSuccess: (body: Object) => void = options.onSuccess;
    const onError: (event: Error, body?: Object) => void = options.onError;
    const onProgress: (event: { percent: number }) => void = options.onProgress;
    const file: File = options.file;

    const user = firebase.auth.currentUser;

    const storage = firebase.storage;

    const storageRef = ref(storage, `images/${user?.uid}/${file.name}`);

    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
    });

    setLoadingImage(true);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress({ percent: progress });
      },
      (error) => {
        console.error("Error uploading file", error);
        onError(error);
      },
      () => {
        setLoadingImage(false);
        onSuccess("Ok");
      }
    );

    uploadBytes(storageRef, file)
      .then((snapshot) => {
        onSuccess("Ok");
      })
      .catch((error) => {
        console.error("Error uploading file", error);
        onError(error);
      });
  };

  const handleDelete = (key: any) => {
    const newData = eventsList.filter((item: any) => item.key !== key);
    setEventsList(newData);
  };

  const handleOnChange = ({ file, fileList }: any) => {
    setUploadList(fileList);
    handlePreview(file);
  };

  const handleProcessIA = async (e: SyntheticEvent) => {
    e.preventDefault();

    const user = firebase.auth.currentUser;

    setLoadingTable(true);

    const storage = firebase.storage;

    // @ts-ignore
    const storageRef = ref(
      storage,
      `images/${user?.uid}/${uploadList[0].name}`
    ).toString();
    const contentType = uploadList[0].type;

    try {
      const imageAnalyzeResult = await firebase.functions.analyzeImage({
        media: {
          url: storageRef,
          contentType,
        },
        taggingType: tagType,
        prompt,
      });

      let data = imageAnalyzeResult.data as AnalyzeImageResponse;

      const events = data.events.map((event, index) => {
        return {
          ...event,
          paramsCount: event.parameters.length,
        };
      });

      // Flatten the parameters for make unique rows in a single list with the other properties
      const flatten = events.flatMap((event) => {
        return event.parameters.map((param) => {
          return {
            event: event.event,
            trigger: event.trigger,
            tag_script: event.tag_script,
            param_name: param.param_name,
            param_value: param.param_value,
            example_value: param.example_value,
            paramsCount: event.paramsCount,
          };
        });
      });

      setEventsList(data.events);
    } catch (error) {
      console.error("Error analyzing image", error);
    } finally {
      setLoadingTable(false);
    }
  };

  const handlePreview = async (file: any) => {
    if (!file.url && !file.preview) {
      file.preview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    setPreviewImage(file.url || file.preview);
  };

  const onChangePrompt = (e: any) => {
    setPrompt(e.target.value);
  };

  const { getValidToken } = useGoogleToken();

  const handleSaveGoogleSheet = async () => {
    setSavingToGoogleSheet(true);

    try {
      const freshToken = await getValidToken();
      
      const result = await firebase.functions.saveToGoogleSheet({
        token: freshToken,
        rows: eventsList,
      });

      const data = result.data as any;
      const sheetUrl = `https://docs.google.com/spreadsheets/d/${data.spreadsheetId}`;
      setSheetUrl(sheetUrl);
      
    } catch (error) {
      console.error("Error saving to Google Sheet", error);
    } finally {
      setSavingToGoogleSheet(false);
    }
  };

  const showUploadList = {
    showDownloadIcon: false, // Eliminar el ícono de procesamiento por imagen
    showRemoveIcon: true,
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 32,
          alignItems: "flex-start",
          minHeight: 400,
        }}
      >
        {/* Preview de imagen grande a la izquierda */}
        <div
          style={{
            flex: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: 350,
          }}
        >
          {previewImage ? (
            <Spin tip="Cargando" spinning={loadingImage}>
              <Image
                style={{
                  maxHeight: 400,
                  maxWidth: "100%",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px #0001",
                }}
                src={previewImage}
              />
            </Spin>
          ) : (
            <div
              style={{
                width: 400,
                height: 300,
                background: "#fafafa",
                border: "1px dashed #d9d9d9",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#bbb",
              }}
            >
              No hay imagen cargada
            </div>
          )}
        </div>
        {/* Controles a la derecha */}
        <div
          style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}
        >
          <Upload
            showUploadList={showUploadList}
            customRequest={uploadImage}
            onChange={handleOnChange}
            accept="image/*"
            listType="picture"
            fileList={uploadList}
          >
            <Button
              icon={<UploadOutlined />}
              disabled={uploadList.length > 0}
              block
            >
              Cargar imagen
            </Button>
          </Upload>
          <Select
            value={tagType}
            style={{ width: "100%" }}
            onChange={setTagType}
            options={[{ label: "Ecommerce", value: "ecommerce" }]}
            disabled
          />
          <TextArea
            onChange={onChangePrompt}
            rows={4}
            placeholder="Prompt"
            value={prompt}
            style={{ width: "100%", resize: "none" }}
          />
          {/* Botón principal de procesamiento */}
          <Button
            type="primary"
            icon={loadingTable ? <Spin size="small" /> : <PlayCircleOutlined />}
            size="large"
            style={{ fontWeight: 600, marginTop: 8, marginBottom: 8 }}
            block
            onClick={handleProcessIA}
            disabled={uploadList.length === 0 || loadingTable}
          >
            {loadingTable
              ? "Procesando..."
              : `Procesar imagen${uploadList.length > 1 ? "es" : ""}`}
          </Button>
          {/* Botones secundarios menos relevantes */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 8,
              marginTop: 8,
            }}
          >
            <Spin spinning={savingToGoogleSheet}>
              <Button
                type="default"
                disabled={!(eventsList.length > 0)}
                onClick={handleSaveGoogleSheet}
                style={{ minWidth: 160 }}
              >
                Guardar en Google Sheets
              </Button>
            </Spin>
            <Typography.Link
              href={sheetUrl}
              target="_blank"
              disabled={sheetUrl === ""}
              style={{ alignSelf: "center" }}
            >
              Ver hoja de cálculo
            </Typography.Link>
          </div>
        </div>
      </div>
      {/* Tabla de eventos debajo */}
      <div style={{ marginTop: 32 }}>
        <Table dataSource={eventsList} columns={columns} />
      </div>
    </>
  );
};

export default Workspace;
